import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Table, Button, Space, Tag, Card, Typography, Row, Col, message, Progress, Tooltip, Divider, Modal, List, Badge } from 'antd';
import { InboxOutlined, RollbackOutlined, PlusOutlined, StopOutlined, EditOutlined, DeleteOutlined, VideoCameraOutlined, YoutubeOutlined, LinkOutlined, PlayCircleOutlined, PauseCircleOutlined, DatabaseOutlined, CloudDownloadOutlined, CloudUploadOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, EyeOutlined, LoadingOutlined, WarningOutlined, CalendarOutlined } from '@ant-design/icons';
import { useOutletContext } from 'react-router-dom';
import { formatDuration, getStatusColor, formatRelativeTime, formatUptime, getMonitoringBadgeStatus, isMonitoringStale } from './utils';
import { useStreamsData } from './hooks/useStreamsData';
import { usePlayoutStatuses } from './hooks/usePlayoutStatuses';
import { usePreviewPlayout } from './hooks/usePreviewPlayout';
import {
  archiveStream,
  cancelStream,
  deleteStream,
  fetchPlayoutStatus,
  getStreamWithVideos,
  startPlayout,
  stopPlayout,
  transcodeStream,
  unarchiveStream
} from './api';
import StreamEditorModal, { StreamEditorTabKey } from './StreamEditorModal';
import ManageVideoModal from './ManageVideoModal';
import AssignPlayoutServerModal from './AssignPlayoutServerModal';
import TranscodeJobsModal from './TranscodeJobsModal';
import StartPlayoutModal from './StartPlayoutModal';
import StreamFilters from './StreamFilters';
import PreviewModal from './PreviewModal';
import { YouTubeLiveStream, StreamStatus, MonitoringDetails } from './types';
import { ComponentTitle } from '../custom-components';

const { Text } = Typography;

interface StreamListProps {
  onCreateStream?: () => void;
}

const YouTubeLiveStreamList: React.FC<StreamListProps> = ({ onCreateStream }) => {
  const { distributionPlatformData }: any = useOutletContext();
  const {
    streams,
    filteredStreams,
    loading,
    loadStreams,
    searchText,
    setSearchText,
    statusFilter,
    setStatusFilter,
    outputTargetFilter,
    setOutputTargetFilter,
    dateFilter,
    setDateFilter,
    showArchived,
    setShowArchived,
    pageSize,
    setPageSize
  } = useStreamsData();
  const [selectedStream, setSelectedStream] = useState<YouTubeLiveStream | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editorStream, setEditorStream] = useState<YouTubeLiveStream | null>(null);
  const [editorInitialTab, setEditorInitialTab] = useState<StreamEditorTabKey>('details');
  const [showManageVideos, setShowManageVideos] = useState(false);
  const [showAssignServer, setShowAssignServer] = useState(false);
  const [streamToAssign, setStreamToAssign] = useState<YouTubeLiveStream | null>(null);
  const [showTranscodeJobs, setShowTranscodeJobs] = useState(false);
  const [streamToTranscode, setStreamToTranscode] = useState<YouTubeLiveStream | null>(null);
  const [transcodeLoading, setTranscodeLoading] = useState<string | null>(null);
  const [playoutActionLoading, setPlayoutActionLoading] = useState<string | null>(null);
  // Stream whose playout is currently being prepared (download + transcode of
  // the first file). Drives a disabled "Starting…" row button so a second Start
  // can't churn the prep (the playout-server rejects it while state==='starting').
  const [startingStreamId, setStartingStreamId] = useState<string | null>(null);
  const [showStartPlayoutModal, setShowStartPlayoutModal] = useState(false);
  const [streamToStart, setStreamToStart] = useState<YouTubeLiveStream | null>(null);
  const [startFromMinuteInput, setStartFromMinuteInput] = useState<string>('0');
  const [showConfigErrorsModal, setShowConfigErrorsModal] = useState(false);
  const [configErrors, setConfigErrors] = useState<string[]>([]);
  const [configErrorsStreamName, setConfigErrorsStreamName] = useState<string>('');
  const startPlayoutPollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startPlayoutMessageRef = useRef<any>(null);
  const {
    previewStream,
    showPreview,
    openPreview,
    closePreview
  } = usePreviewPlayout();

  // Playout server status map: playoutServerId -> status
  const { statusMap: playoutServerStatus, refreshStatuses: fetchPlayoutServerStatuses } = usePlayoutStatuses(streams);

  const reloadStreamsWithStatus = useCallback(async () => {
    const data = await loadStreams();
    if (data) {
      fetchPlayoutServerStatuses(data);
    }
  }, [fetchPlayoutServerStatuses, loadStreams]);

  // Transcode status is now only checked in TranscodeJobsModal when opened

  // Load streams on component mount
  useEffect(() => {
    reloadStreamsWithStatus().catch(error => {
      console.error('Error loading streams:', error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (startPlayoutPollIntervalRef.current) {
        clearInterval(startPlayoutPollIntervalRef.current);
      }
      if (startPlayoutMessageRef.current) {
        message.destroy();
        startPlayoutMessageRef.current = null;
      }
    };
  }, []);

  const handleRefreshServerStatus = async (playoutServerId: string) => {
    console.log(`Manually refreshing status for server: ${playoutServerId}`);
    await fetchPlayoutServerStatuses(streams, false, playoutServerId);
  };

  const openConfigErrors = (errors: string[], streamName?: string) => {
    setConfigErrors(errors);
    setConfigErrorsStreamName(streamName || '');
    setShowConfigErrorsModal(true);
  };

  // Transcode status checking removed from main view - only happens in TranscodeJobsModal

  const handleCreateStream = () => {
    setEditorStream(null);
    setEditorInitialTab('details');
    setShowEditor(true);
    onCreateStream?.();
  };

  const handleEditStream = (stream: YouTubeLiveStream) => {
    setEditorStream(stream);
    setEditorInitialTab('details');
    setShowEditor(true);
  };

  // Amagi HLS-pull streams: jump straight to the editor's Schedule tab
  // (imported schedule chunks replace catalog video management).
  const handleOpenSchedule = (stream: YouTubeLiveStream) => {
    setEditorStream(stream);
    setEditorInitialTab('schedule');
    setShowEditor(true);
  };

  const handleManageVideos = async (stream: YouTubeLiveStream) => {
    try {
      const streamWithVideos = await getStreamWithVideos(stream.id);
      setSelectedStream(streamWithVideos);
      setShowManageVideos(true);
    } catch (error) {
      console.error('Error loading stream for video management:', error);
    }
  };

  const handleDeleteStream = async (streamId: string) => {
    try {
      await deleteStream(streamId);
      reloadStreamsWithStatus();
    } catch (error) {
      console.error('Error deleting stream:', error);
    }
  };

  const handleArchiveStream = async (stream: YouTubeLiveStream, archive: boolean) => {
    try {
      await (archive ? archiveStream(stream.id) : unarchiveStream(stream.id));
      message.success(archive ? `Archived "${stream.name}"` : `Unarchived "${stream.name}"`);
      reloadStreamsWithStatus();
    } catch (error: any) {
      console.error('Error archiving stream:', error);
      message.error(error?.response?.data?.error || 'Failed to update archive state');
    }
  };

  const handleCancelStream = async (streamId: string) => {
    try {
      await cancelStream(streamId);
      reloadStreamsWithStatus();
    } catch (error) {
      console.error('Error cancelling stream:', error);
    }
  };

  const handlePreviewStream = (stream: YouTubeLiveStream) => {
    openPreview(stream);
  };

  const handleAssignPlayoutServer = (stream: YouTubeLiveStream) => {
    setStreamToAssign(stream);
    setShowAssignServer(true);
  };

  const handleTranscodeVideos = async (stream: YouTubeLiveStream) => {
    setTranscodeLoading(stream.id);
    try {
      // First, ensure we have the stream with videos loaded
      let streamWithVideos = stream;
      if (!stream.videos || stream.videos.length === 0) {
        streamWithVideos = await getStreamWithVideos(stream.id);
      }

      const transcodeResponse = await transcodeStream(stream.id);
      
      const jobsCreated = transcodeResponse.jobs_created || 0;
      const jobsSkipped = transcodeResponse.jobs_skipped || 0;
      
      if (jobsSkipped > 0) {
        message.warning(
          `Created ${jobsCreated} new job(s), skipped ${jobsSkipped} existing job(s) (only ERROR jobs are recreated)`,
          5
        );
      } else {
        message.success(`Transcoding jobs created: ${jobsCreated} of ${transcodeResponse.total_videos} videos`);
      }
      
      // Show modal with jobs
      setStreamToTranscode(streamWithVideos);
      setShowTranscodeJobs(true);
    } catch (error: any) {
      console.error('Error triggering transcoding:', error);
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error('Failed to trigger transcoding jobs');
      }
    } finally {
      setTranscodeLoading(null);
    }
  };

  const handleViewTranscodeJobs = async (stream: YouTubeLiveStream) => {
    setStreamToTranscode(stream);
    setShowTranscodeJobs(true);
  };

  const handleTranscodeJobsClose = () => {
    setShowTranscodeJobs(false);
    setStreamToTranscode(null);
    // No need to refresh transcode status here - only fetch when modal is open
  };

  const handleStartPlayout = async (stream: YouTubeLiveStream) => {
    if (!stream.playout_server_id) {
      message.error('No playout server assigned to this stream');
      return;
    }

    if (stream.configuration_errors && stream.configuration_errors.length > 0) {
      openConfigErrors(stream.configuration_errors, stream.name);
      return;
    }

    // Show modal to prompt for startFromMinute
    setStreamToStart(stream);
    setStartFromMinuteInput('0');
    setShowStartPlayoutModal(true);
  };

  const handleConfirmStartPlayout = async () => {
    if (!streamToStart) return;

    const startFromMinute = parseInt(startFromMinuteInput, 10);
    if (isNaN(startFromMinute) || startFromMinute < 0) {
      message.error('Please enter a valid number (0 or greater)');
      return;
    }

    setShowStartPlayoutModal(false);

    // Open the preview immediately so the user watches download + transcode
    // progress instead of a long row spinner. PreviewModal keeps the player
    // hidden until playout is actually live, then switches to it on its own.
    // Feedback now lives in the modal, so we don't hold the row button spinning.
    openPreview(streamToStart);
    // Mark this stream as starting so its row button shows a disabled
    // "Starting…" until playout is live (or fails) — prevents a second Start.
    setStartingStreamId(streamToStart.id);

    // Send start request asynchronously (fire and forget)
    // Don't wait for it to complete as it may timeout during first file download
    startPlayout(streamToStart.id, startFromMinute, 10000).catch((error: any) => {
      const errors = error?.response?.data?.configuration_errors;
      if (Array.isArray(errors) && errors.length > 0) {
        if (startPlayoutPollIntervalRef.current) {
          clearInterval(startPlayoutPollIntervalRef.current);
          startPlayoutPollIntervalRef.current = null;
        }
        if (startPlayoutMessageRef.current) {
          message.destroy();
          startPlayoutMessageRef.current = null;
        }
        setPlayoutActionLoading(null);        setStartingStreamId(null);
        openConfigErrors(errors, streamToStart?.name);
        reloadStreamsWithStatus();
        return;
      }
      // If the server returned an actual error response (not a timeout), show it and stop polling
      if (error?.response) {
        if (startPlayoutPollIntervalRef.current) {
          clearInterval(startPlayoutPollIntervalRef.current);
          startPlayoutPollIntervalRef.current = null;
        }
        if (startPlayoutMessageRef.current) {
          message.destroy();
          startPlayoutMessageRef.current = null;
        }
        setPlayoutActionLoading(null);        setStartingStreamId(null);
        const errorMsg = error.response.data?.error || 'Failed to start playout';
        message.error(errorMsg);
        reloadStreamsWithStatus();
        return;
      }
      // No response = timeout or network error — continue polling
      console.log('Start playout request sent (may timeout during download):', error);
    });
    
    // Start aggressive polling for playout status
    const startPolling = () => {
      // Clear any existing polling
      if (startPlayoutPollIntervalRef.current) {
        clearInterval(startPlayoutPollIntervalRef.current);
      }

      let pollCount = 0;
      const maxPolls = 300; // Poll for up to 5 minutes (300 * 1 second)
      
      startPlayoutPollIntervalRef.current = setInterval(async () => {
        pollCount++;
        
        if (!streamToStart || !streamToStart.playout_server_id) {
          // Stream was cleared, stop polling
          if (startPlayoutPollIntervalRef.current) {
            clearInterval(startPlayoutPollIntervalRef.current);
            startPlayoutPollIntervalRef.current = null;
          }
          if (startPlayoutMessageRef.current) {
            message.destroy();
            startPlayoutMessageRef.current = null;
          }
          setPlayoutActionLoading(null);          setStartingStreamId(null);
          return;
        }

        try {
          // Fetch playout status only to detect when ffmpeg goes live so we can
          // flip the row out of "Starting…" and refresh. The preview modal polls
          // its own status independently for the detailed download/transcode view.
          const playoutStatus = await fetchPlayoutStatus(streamToStart.id);
          const isRunning = playoutStatus?.ffmpeg?.isRunning;
          
          if (isRunning) {
            // Stream is playing!
            if (startPlayoutMessageRef.current) {
              message.destroy();
              startPlayoutMessageRef.current = null;
            }
            message.success('Playout started and is now streaming!');
            // Preview is already open (opened eagerly on Start); it switches to
            // the player itself once HLS is live — no need to re-open here.
            if (startPlayoutPollIntervalRef.current) {
              clearInterval(startPlayoutPollIntervalRef.current);
              startPlayoutPollIntervalRef.current = null;
            }
            setPlayoutActionLoading(null);            setStartingStreamId(null);
            // Update status and refresh streams
            await fetchPlayoutServerStatuses([streamToStart], false, streamToStart.playout_server_id);
            reloadStreamsWithStatus();
            return;
          }
          
          if (pollCount >= maxPolls) {
            // Timeout after max polls
            if (startPlayoutMessageRef.current) {
              message.destroy();
              startPlayoutMessageRef.current = null;
            }
            message.warning('Playout start is taking longer than expected. Please check the server status.');
            if (startPlayoutPollIntervalRef.current) {
              clearInterval(startPlayoutPollIntervalRef.current);
              startPlayoutPollIntervalRef.current = null;
            }
            setPlayoutActionLoading(null);            setStartingStreamId(null);
            reloadStreamsWithStatus();
            return;
          }
        } catch (error) {
          console.error('Error polling playout status:', error);
          // Continue polling even on error
        }
      }, 2000); // Poll every 2 seconds
    };

    // Start polling immediately
    startPolling();
    
    // Also refresh streams to get updated status
    reloadStreamsWithStatus();
  };

  const handleStopPlayout = async (stream: YouTubeLiveStream) => {
    setPlayoutActionLoading(stream.id);
    
    // Clear any active start polling
    if (startPlayoutPollIntervalRef.current) {
      clearInterval(startPlayoutPollIntervalRef.current);
      startPlayoutPollIntervalRef.current = null;
    }
    
    try {
      await stopPlayout(stream.id);
      message.success('Playout stopped successfully');
      reloadStreamsWithStatus();
    } catch (error: any) {
      console.error('Error stopping playout:', error);
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error('Failed to stop playout');
      }
    } finally {
      setPlayoutActionLoading(null);
    }
  };

  const getYouTubeEmbedUrl = (url: string) => {
    // Convert watch URL to embed URL
    // https://www.youtube.com/watch?v=VIDEO_ID -> https://www.youtube.com/embed/VIDEO_ID
    const videoId = url.split('v=')[1]?.split('&')[0];
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  const isLocalTestingStream = (stream: YouTubeLiveStream): boolean => {
    // Check if it's a local nginx-rtmp stream
    // Could be "rtmp://nginx-rtmp:1935/live" with stream_key or "rtmp://nginx-rtmp:1935/live/stream"
    return !!stream.rtmp_url && stream.rtmp_url.startsWith('rtmp://nginx-rtmp:1935/live');
  };

  const getHlsUrl = (stream: YouTubeLiveStream) => {
    // The stream's HLS manifest lives at <playout-server-endpoint>/hls/main.m3u8.
    // Caddy serves /hls/* unauthenticated (it's the primary production output —
    // Amagi pulls the same URL) so this works from any browser context that can
    // reach the playout-server's Caddy front door.
    //
    // Route history: `/hls/<streamKey>.m3u8` on a separate port (pre-v1
    // nginx-rtmp) → `/preview/main.m3u8` (v1, preview-only era) → `/hls/main.m3u8`
    // (canonical; servers keep /preview/* as a deprecated alias during the
    // transition — requires playout servers deployed at/after the rename).
    // Caddy multiplexes by path on the API port; one playout-server = one HLS
    // playlist (only one stream runs at a time), so there's no streamKey.
    //
    // Returns '' when no playout server is assigned; caller should treat that
    // as "no preview URL available" and disable the player.
    const endpoint = stream.playout_server?.endpoint_url;
    if (!endpoint) return '';
    return `${endpoint.replace(/\/+$/, '')}/hls/main.m3u8`;
  };

  const getEpgUrl = (stream: YouTubeLiveStream) => {
    // XMLTV EPG served next to the manifest at <endpoint>/hls/epg.xml (same
    // unauthenticated Caddy front door). '' when no playout server is assigned.
    const endpoint = stream.playout_server?.endpoint_url;
    if (!endpoint) return '';
    return `${endpoint.replace(/\/+$/, '')}/hls/epg.xml`;
  };

  const getPreviewPageUrl = (stream: YouTubeLiveStream) => {
    // Browser HLS player page the playout server installs at the root of its
    // HLS dir (<endpoint>/preview/ — trailing slash required until servers pick
    // up the bare-path redirect). Opens in a new tab: playout is plain HTTP, so
    // embedding it here would trip the mixed-content block, same as the manifest.
    const endpoint = stream.playout_server?.endpoint_url;
    if (!endpoint) return '';
    return `${endpoint.replace(/\/+$/, '')}/preview/`;
  };


  const columns = [
    {
      title: 'Stream Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: YouTubeLiveStream) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {text}
            {isLocalTestingStream(record) && (
              <Tag color="blue" style={{ marginLeft: '8px', fontSize: '10px' }}>
                Local Testing
              </Tag>
            )}
            {record.archived_at && (
              <Tag style={{ marginLeft: '8px', fontSize: '10px' }}>
                Archived
              </Tag>
            )}
          </div>
          {record.description && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              {record.description.length > 100 
                ? `${record.description.substring(0, 100)}...` 
                : record.description}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Output Target',
      dataIndex: 'output_target',
      key: 'output_target',
      render: (output_target: string) => {
        if (output_target === 'amagi_hls') {
          return (
            <Space size={4} direction="vertical" align="center">
              <img
                src="https://awsmp-logos.s3.amazonaws.com/d6e7bb42-ae72-46ad-a506-48157a8f1db6/2cb09622d3da084309e387d62ebab60d.png"
                alt="Amagi"
                style={{ height: 16, width: 'auto', display: 'block' }}
              />
              <Tag color="blue" style={{ fontSize: '10px', margin: 0 }}>
                HLS
              </Tag>
            </Space>
          );
        }
        if (output_target === 'youtube') {
          return (
            <Space size={4} direction="vertical" align="center">
              <img
                src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQQmHg1bt94OKYExipTQZ6oPHQg9DjLqiLgT1Px-boCMg&s=10"
                alt="YouTube"
                style={{ height: 16, width: 'auto', display: 'block' }}
              />
              <Tag style={{ fontSize: '10px', margin: 0 }}>
                RTMP
              </Tag>
            </Space>
          );
        }
        return (
          <Tag color="blue" style={{ fontSize: '10px' }}>
            {output_target}
          </Tag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: StreamStatus, record: YouTubeLiveStream) => (
        <Space size={4} wrap>
          <Tag color={getStatusColor(status)}>
            {status?.toUpperCase()}
          </Tag>
          {record.video_selection_mode === 'automatic' && (
            <Tag color="purple" style={{ fontSize: '10px' }}>
              Auto Videos
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Scheduled Start',
      dataIndex: 'scheduled_start_time',
      key: 'scheduled_start_time',
      render: (date: string) => date ? new Date(date).toLocaleString() : '-',
    },
    {
      title: 'Duration',
      dataIndex: 'total_duration_seconds',
      key: 'duration',
      render: (seconds: number) => seconds ? formatDuration(seconds) : '-',
    },
    {
      title: 'Privacy',
      dataIndex: 'privacy_status',
      key: 'privacy_status',
      render: (privacy: string) => (
        <Tag color={privacy === 'public' ? 'green' : privacy === 'unlisted' ? 'blue' : 'red'}>
          {privacy}
        </Tag>
      ),
    },
    {
      title: 'Health',
      dataIndex: 'playout_server',
      key: 'health',
      width: 300,
      render: (playout_server: any, record: YouTubeLiveStream) => {
        if (!playout_server || !record.playout_server_id) {
          return <Text type="secondary">Not assigned</Text>;
        }

        const status = playoutServerStatus[record.playout_server_id];
        const isPolled = status !== undefined; // whether we've attempted to check this server
        const isReachable = status?.reachable;
        const ffmpegRunning = status?.ffmpeg.isRunning;
        const queue = status?.downloadQueue;
        const md = record.monitoring_details as MonitoringDetails | undefined;
        const isLiveOAuth = record.status === 'live' && record.stream_mode === 'oauth';

        // Build rich tooltip content for monitoring badge
        const monitoringTooltip = md ? (
          <div>
            <div style={{ marginBottom: 4 }}>Last checked: {formatRelativeTime(record.last_monitored_at)}</div>
            <div>Playout: {md.playout
              ? `${md.playout.server_reachable ? 'Online' : 'Offline'}${md.playout.ffmpeg_running ? ', FFmpeg running' : ', FFmpeg idle'}${md.playout.uptime ? `, up ${formatUptime(md.playout.uptime)}` : ''}`
              : 'No data'}</div>
            {md.youtube_ingest && (
              <div>YouTube: {md.youtube_ingest.stream_status ? `Stream ${md.youtube_ingest.stream_status}` : 'Unknown'}{md.youtube_ingest.health_status ? `, health ${md.youtube_ingest.health_status}` : ''}</div>
            )}
            {md.hls_output && (
              <div>HLS output: {md.hls_output.severity}{md.hls_output.reason ? ` — ${md.hls_output.reason}` : ''}</div>
            )}
            {md.broadcasts && md.broadcasts.length > 0 && (
              <div>Broadcasts: {md.broadcasts.map(b => `${b.id} (${b.status})`).join(', ')}</div>
            )}
            <div>Token: {md.token ? (md.token.valid ? 'Valid' : `Invalid${md.token.error ? ` - ${md.token.error}` : ''}`) : 'No data'}</div>
          </div>
        ) : 'Not monitored';

        return (
          <div>
            {/* Row 1: Monitoring badge + server name + refresh */}
            <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Tooltip title={monitoringTooltip}>
                <Badge status={getMonitoringBadgeStatus(record.monitoring_status)} />
              </Tooltip>
              {playout_server.display_name || playout_server.name}
              <Tooltip title="Refresh server status">
                <Button
                  type="text"
                  size="small"
                  icon={<ReloadOutlined spin={isPolled && isReachable === undefined} />}
                  onClick={() => handleRefreshServerStatus(record.playout_server_id!)}
                  style={{ padding: '0 4px', height: '20px', minWidth: '20px' }}
                />
              </Tooltip>
            </div>
            {playout_server.display_name && (
              <div style={{ fontSize: '11px', color: '#999', marginTop: '1px' }}>
                {playout_server.name}
              </div>
            )}
            {/* Row 2: Real-time playout status */}
            <div style={{ fontSize: '11px' }}>
              {!isPolled ? (
                <Tag color="default">idle</Tag>
              ) : isReachable === undefined ? (
                <Tooltip title="Checking server status...">
                  <Tag icon={<LoadingOutlined />} color="processing">checking</Tag>
                </Tooltip>
              ) : isReachable === false ? (
                <Tooltip title={status?.error || 'Server unreachable'}>
                  <Tag color="error">unreachable</Tag>
                </Tooltip>
              ) : (
                <>
                  <Tag color="success">online</Tag>
                  {status && (
                    <Tooltip title={`FFmpeg ${ffmpegRunning ? 'is running' : 'is idle'}`}>
                      <Tag color={ffmpegRunning ? 'processing' : 'default'}>
                        {ffmpegRunning ? 'streaming' : 'idle'}
                        {ffmpegRunning && status.ffmpeg.uptime ? ` ▲${formatUptime(status.ffmpeg.uptime)}` : ''}
                        {ffmpegRunning && !status.ffmpeg.uptime && md?.playout?.uptime ? ` ▲${formatUptime(md.playout.uptime)}` : ''}
                      </Tag>
                    </Tooltip>
                  )}
                  {queue && queue.isActive && (
                    <>
                      <Tooltip title={
                        <div>
                          <div><strong>Queue Status:</strong></div>
                          <div>Pending: {queue.stateCounts.pending}</div>
                          <div>Downloading: {queue.stateCounts.downloading}</div>
                          <div>Ready: {queue.stateCounts.ready}</div>
                          <div>Playing: {queue.stateCounts.playing}</div>
                          <div>Completed: {queue.stateCounts.completed}</div>
                          {queue.stateCounts.failed > 0 && (
                            <div style={{color: '#ff4d4f'}}>Failed: {queue.stateCounts.failed}</div>
                          )}
                        </div>
                      }>
                        <Tag color="blue" icon={<CloudDownloadOutlined />}>
                          {queue.stateCounts.ready + queue.stateCounts.downloading} cached
                        </Tag>
                      </Tooltip>
                      {queue.currentlyPlaying && (
                        <Tooltip title={`Playing: ${queue.currentlyPlaying.videoTitle}`}>
                          <Tag color="green" icon={<PlayCircleOutlined />}>
                            {queue.currentlyPlaying.index + 1}/{queue.totalFiles}
                          </Tag>
                        </Tooltip>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
            {/* Row 3: YouTube ingest (live OAuth streams with monitoring data only) */}
            {isLiveOAuth && md?.youtube_ingest && (
              <div style={{ fontSize: '11px', marginTop: 2 }}>
                <Tag color={
                  md.youtube_ingest.health_status === 'good' ? 'green'
                  : md.youtube_ingest.health_status === 'ok' ? 'green'
                  : md.youtube_ingest.health_status === 'bad' ? 'red'
                  : 'default'
                }>
                  YT: {md.youtube_ingest.health_status || 'noData'}
                </Tag>
                {md.youtube_ingest.configuration_issues && md.youtube_ingest.configuration_issues.length > 0 && (
                  <Tooltip title={
                    <div>
                      {md.youtube_ingest.configuration_issues.map((issue, i) => (
                        <div key={i}>{issue.reason || issue.description || issue.type || 'Unknown issue'}</div>
                      ))}
                    </div>
                  }>
                    <Tag color="orange" icon={<WarningOutlined />}>
                      {md.youtube_ingest.configuration_issues.length} issue{md.youtube_ingest.configuration_issues.length !== 1 ? 's' : ''}
                    </Tag>
                  </Tooltip>
                )}
              </div>
            )}
            {/* Row 4: Broadcast status */}
            {md?.broadcasts && md.broadcasts.length > 0 && (
              <div style={{ fontSize: '11px', marginTop: 2 }}>
                {md.broadcasts.map((b) => (
                  <Tag
                    key={b.id}
                    color={
                      b.status === 'live' ? 'green'
                      : b.status === 'testing'
                        ? (record.scheduled_start_time && Date.now() > new Date(record.scheduled_start_time).getTime() + 10 * 60 * 1000 ? 'orange' : 'blue')
                      : b.status === 'complete' ? 'purple'
                      : b.status === 'revoked' ? 'red'
                      : b.status === 'created' ? 'default'
                      : b.status === 'ready'
                        ? (record.scheduled_start_time && Date.now() > new Date(record.scheduled_start_time).getTime() + 10 * 60 * 1000 ? 'orange' : 'cyan')
                      : 'default'
                    }
                  >
                    BC: {b.status}
                  </Tag>
                ))}
              </div>
            )}
            {/* Row 5: Alerts */}
            {md && (
              <div style={{ fontSize: '11px', marginTop: 2 }}>
                {md.token && !md.token.valid && (
                  <Tooltip title={md.token.error || 'OAuth token is invalid'}>
                    <Tag color="error">Token Invalid</Tag>
                  </Tooltip>
                )}
                {record.status === 'live' && md.broadcasts?.some(b => {
                  if (b.status !== 'testing' && b.status !== 'ready') return false;
                  const scheduledStart = record.scheduled_start_time;
                  if (!scheduledStart) return false;
                  const scheduledTime = new Date(scheduledStart).getTime();
                  return Date.now() > scheduledTime + 10 * 60 * 1000;
                }) && (
                  <Tag color="error">Broadcast not live</Tag>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Last Checked',
      dataIndex: 'last_monitored_at',
      key: 'last_monitored_at',
      width: 110,
      render: (lastMonitoredAt: string | undefined) => {
        if (!lastMonitoredAt) return <Text type="secondary">-</Text>;
        const stale = isMonitoringStale(lastMonitoredAt);
        return (
          <Tooltip title={new Date(lastMonitoredAt).toLocaleString()}>
            <Text style={stale ? { color: '#faad14' } : undefined}>
              {formatRelativeTime(lastMonitoredAt)}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => date ? new Date(date).toLocaleString() : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 250,
      render: (_: any, record: YouTubeLiveStream) => {
        const configErrors = record.configuration_errors || [];
        const hasConfigErrors = configErrors.length > 0;
        return (
        <Space wrap>
          <Tooltip title="Edit / Configure">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => handleEditStream(record)}
            />
          </Tooltip>
          {record.output_target === 'amagi_hls' ? (
            // Amagi HLS-pull streams get their content from imported schedule
            // chunks, not the video catalog — open the editor's Schedule tab.
            <Tooltip title="Import / Manage Schedule">
              <Button
                type="text"
                icon={<CalendarOutlined />}
                onClick={() => handleOpenSchedule(record)}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Manage Videos">
              <Button
                type="text"
                icon={<VideoCameraOutlined />}
                onClick={() => handleManageVideos(record)}
              />
            </Tooltip>
          )}
          <Tooltip title="Assign Playout Server">
            <Button 
              type="text" 
              icon={<DatabaseOutlined />} 
              onClick={() => handleAssignPlayoutServer(record)}
            />
          </Tooltip>
          {/* YouTube Preview for OAuth streams */}
          {record.video_url && !record.rtmp_url && (
            <Tooltip title="Preview on YouTube">
              <Button 
                type="text" 
                icon={<YoutubeOutlined />} 
                onClick={() => handlePreviewStream(record)}
                style={{ color: '#ff0000' }}
              />
            </Tooltip>
          )}
          {record.video_url && !record.rtmp_url && (
            <Tooltip title="Open YouTube Link">
              <Button 
                type="text" 
                icon={<LinkOutlined />} 
                onClick={() => window.open(record.video_url, '_blank')}
              />
            </Tooltip>
          )}
          {/* Local/Manual Preview for Manual RTMP streams - only show when playing */}
          {(record.playout_server_id || record.video_url) && (
            <Tooltip title="Preview Stream">
              <Button 
                type="text" 
                icon={<EyeOutlined />} 
                onClick={() => handlePreviewStream(record)}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>
          )}
          
          {hasConfigErrors && (
            <Tag
              color="error"
              style={{ cursor: 'pointer', margin: 0 }}
              onClick={() => openConfigErrors(configErrors, record.name)}
            >
              Config Errors
            </Tag>
          )}
          {/* Show transcode button only if stream has videos and CEA embedded captions enabled */}
          {(record.videos && record.videos.length > 0 && record.cea_embedded_captions === true) && (
            <>
              <Tooltip title="Transcode videos with embedded captions for YouTube Live">
                <Button 
                  type="default"
                  size="small"
                  icon={<CloudUploadOutlined />}
                  onClick={() => handleTranscodeVideos(record)}
                  loading={transcodeLoading === record.id}
                >
                  Transcode
                </Button>
              </Tooltip>
              <Tooltip title="View transcoding jobs">
                <Button 
                  type="text"
                  size="small"
                  icon={<VideoCameraOutlined />}
                  onClick={() => handleViewTranscodeJobs(record)}
                >
                  Jobs
                </Button>
              </Tooltip>
            </>
          )}
          {(record.status === 'configured' || record.status === 'live' || record.status === 'manual_scheduled') && record.playout_server_id && (
            <>
              <Tooltip title={
                startingStreamId === record.id
                  ? 'Stream is starting — preparing the first file (download + transcode)…'
                  : hasConfigErrors
                  ? 'Resolve configuration errors before starting'
                  : playoutServerStatus[record.playout_server_id]?.reachable === undefined
                  ? 'Checking server status...'
                  : !playoutServerStatus[record.playout_server_id]?.reachable
                  ? `Server unreachable: ${playoutServerStatus[record.playout_server_id]?.error || 'Unknown error'}`
                  : playoutServerStatus[record.playout_server_id]?.ffmpeg.isRunning
                  ? 'FFmpeg is already running'
                  : 'Start Playout. Make sure videos are transcoded and assigned to the stream first.'
              }>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlayCircleOutlined />}
                  onClick={() => handleStartPlayout(record)}
                  loading={playoutActionLoading === record.id}
                  disabled={
                    startingStreamId === record.id ||
                    hasConfigErrors ||
                    !playoutServerStatus[record.playout_server_id]?.reachable ||
                    playoutServerStatus[record.playout_server_id]?.reachable === undefined ||
                    playoutServerStatus[record.playout_server_id]?.ffmpeg.isRunning
                  }
                >
                  {startingStreamId === record.id ? 'Starting…' : 'Start'}
                </Button>
              </Tooltip>
              <Tooltip title={
                playoutServerStatus[record.playout_server_id]?.reachable === undefined
                  ? 'Checking server status...'
                  : !playoutServerStatus[record.playout_server_id]?.reachable
                  ? `Server unreachable: ${playoutServerStatus[record.playout_server_id]?.error || 'Unknown error'}`
                  : !playoutServerStatus[record.playout_server_id]?.ffmpeg.isRunning
                  ? 'FFmpeg is not running'
                  : 'Stop Playout'
              }>
                <Button 
                  type="default"
                  size="small"
                  danger
                  icon={<PauseCircleOutlined />}
                  onClick={() => handleStopPlayout(record)}
                  loading={playoutActionLoading === record.id}
                  disabled={
                    !playoutServerStatus[record.playout_server_id]?.reachable ||
                    playoutServerStatus[record.playout_server_id]?.reachable === undefined ||
                    !playoutServerStatus[record.playout_server_id]?.ffmpeg.isRunning
                  }
                >
                  Stop
                </Button>
              </Tooltip>
            </>
          )}
          {record.status === 'configured' && (
            <Tooltip title="Cancel Stream">
              <Button 
                type="text" 
                danger
                icon={<StopOutlined />}
                onClick={() => handleCancelStream(record.id)}
              />
            </Tooltip>
          )}
          <Tooltip title={record.archived_at ? 'Unarchive (show in the default list again)' : 'Archive (hide from the default list; nothing is stopped or deleted)'}>
            <Button
              type="text"
              icon={record.archived_at ? <RollbackOutlined /> : <InboxOutlined />}
              onClick={() => handleArchiveStream(record, !record.archived_at)}
            />
          </Tooltip>
          <Tooltip title="Delete Stream. This will delete the stream and all associated videos and on YouTube (if connected to a YouTube account).">
            <Button 
              type="text" 
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteStream(record.id)}
            />
          </Tooltip>
        </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <ComponentTitle>Live Streams</ComponentTitle>
        </Col>
        <Col>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleCreateStream}
          >
            Create New Stream
          </Button>
        </Col>
      </Row>

      <StreamFilters
        searchText={searchText}
        statusFilter={statusFilter}
        outputTargetFilter={outputTargetFilter}
        dateFilter={dateFilter}
        showArchived={showArchived}
        onSearchChange={setSearchText}
        onStatusChange={setStatusFilter}
        onOutputTargetChange={setOutputTargetFilter}
        onDateChange={setDateFilter}
        onShowArchivedChange={setShowArchived}
        onRefresh={reloadStreamsWithStatus}
      />

      <Card>
        <Table
          columns={columns}
          dataSource={filteredStreams}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} streams`,
            onShowSizeChange: (current, size) => {
              setPageSize(size);
            },
            pageSizeOptions: ['10', '20', '50', '100']
          }}
        />
      </Card>

      <Modal
        title={configErrorsStreamName ? `Config Errors - ${configErrorsStreamName}` : 'Config Errors'}
        open={showConfigErrorsModal}
        onCancel={() => setShowConfigErrorsModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowConfigErrorsModal(false)}>
            Close
          </Button>
        ]}
      >
        {configErrors.length === 0 ? (
          <Text type="secondary">No configuration errors.</Text>
        ) : (
          <List
            dataSource={configErrors}
            renderItem={(item) => <List.Item>{item}</List.Item>}
          />
        )}
      </Modal>
      
      <StreamEditorModal
        open={showEditor}
        stream={editorStream}
        initialTab={editorInitialTab}
        distributionPlatformData={distributionPlatformData}
        onClose={() => {
          setShowEditor(false);
          setEditorStream(null);
        }}
        onSave={() => {
          reloadStreamsWithStatus();
        }}
      />

      {selectedStream && (
        <ManageVideoModal
          stream={selectedStream}
          open={showManageVideos}
          onClose={() => {
            setShowManageVideos(false);
            setSelectedStream(null);
          }}
          onSave={() => {
            setShowManageVideos(false);
            setSelectedStream(null);
            reloadStreamsWithStatus();
          }}
          distributionPlatformData={distributionPlatformData}
        />
      )}



      <AssignPlayoutServerModal
        stream={streamToAssign}
        streams={streams}
        open={showAssignServer}
        onClose={() => {
          setShowAssignServer(false);
          setStreamToAssign(null);
        }}
        onSave={() => {
          setShowAssignServer(false);
          setStreamToAssign(null);
          reloadStreamsWithStatus();
        }}
      />

      <TranscodeJobsModal
        stream={streamToTranscode}
        open={showTranscodeJobs}
        onClose={handleTranscodeJobsClose}
      />

      <StartPlayoutModal
        open={showStartPlayoutModal}
        value={startFromMinuteInput}
        onConfirm={handleConfirmStartPlayout}
        onCancel={() => {
          if (startPlayoutMessageRef.current) {
            message.destroy();
            startPlayoutMessageRef.current = null;
          }
          setShowStartPlayoutModal(false);
          setStreamToStart(null);
          setStartFromMinuteInput('0');        }}
        onChange={setStartFromMinuteInput}
      />

      <PreviewModal
        open={showPreview}
        previewStream={previewStream}
        isStarting={!!previewStream && startingStreamId === previewStream.id}
        closePreview={closePreview}
        isLocalTestingStream={isLocalTestingStream}
        getHlsUrl={getHlsUrl}
        getEpgUrl={getEpgUrl}
        getPreviewPageUrl={getPreviewPageUrl}
        getYouTubeEmbedUrl={getYouTubeEmbedUrl}
      />

    </div>
  );
};

export default YouTubeLiveStreamList;

// {"stream":{"ID":"stream_1760502029889_ifsn0hu6y","NAME":"Test123","DESCRIPTION":"",
//   "SCHEDULED_START_TIME":null,"SCHEDULED_END_TIME":null,
//   "YOUTUBE_BROADCAST_ID":null,"YOUTUBE_STREAM_ID":null,
//   "STATUS":"draft","PRIVACY_STATUS":"public",
//   "CREATED_BY":"4ca465a0-e74d-4343-86d9-8ebc7da966c1",
//   "CREATED_AT":"2025-10-14 21:20:30.343","UPDATED_AT":"2025-10-14 23:03:22.525",
//   "videos":[{"ID":"video_1760508125253_0_qgt2s8ukt",
//   "STREAM_ID":"stream_1760502029889_ifsn0hu6y",
//   "VIDEO_ID":"ef877a2c-1fe6-4b4b-aacb-307b9a20b9b0",
//   "SEQUENCE_ORDER":0,"START_OFFSET_SECONDS":0,"DURATION_SECONDS":2436,
//   "CREATED_AT":"2025-10-14 23:02:05.467",
//   "VIDEO_TITLE":"Your Dog Took a Chunk out of My Finger!","VIDEO_DURATION":2436,
//   "VIDEO_FILE_NAME":"PE-2378_ThePeoplesCourt_s19_ep78_Episode78_Episode_Textless_WB_169_1920x1080_2997_ENG20_final.mp4"}],
//   "total_duration_seconds":null}}
